import React, { useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { motion, AnimatePresence } from 'framer-motion';
import { useEdc } from './EdcContext';
import { EDC_LINEUP, formatSetTime, getDayLabel } from './edcLineup';

// Bubbles drift down the viewport carrying a set suggestion. Click one to
// populate the "I'm Going" form with that set's details. A new bubble spawns
// every SPAWN_MS; each bubble's fall is driven by framer-motion's `animate.y`
// transition. AnimatePresence handles the fade-out/scale-down on exit, whether
// the user pops a bubble or it reaches the bottom of the viewport.

const SPAWN_MS = 2200;
const FALL_SECONDS = 16;
const MAX_BUBBLES = 6;
const GENRE_COLORS = {
  dubstep: '#9b30ff',
  riddim:  '#ff2bd6',
  bass:    '#ffd400',
  dnb:     '#00e5ff',
};

let counter = 0;
function spawnBubble() {
  counter += 1;
  const set = EDC_LINEUP[Math.floor(Math.random() * EDC_LINEUP.length)];
  return {
    key: `${Date.now()}-${counter}`,
    set,
    left: 4 + Math.random() * 88, // % of viewport
    size: 110 + Math.floor(Math.random() * 40), // bubble width px
    duration: FALL_SECONDS + Math.random() * 4,
    color: GENRE_COLORS[set.genre] || '#ffd400',
  };
}

export default function EdcBubbles({ onPick }) {
  const { edcMode, melting, bubblesEnabled } = useEdc();
  const [bubbles, setBubbles] = useState([]);

  const active = edcMode && !melting && bubblesEnabled;

  useEffect(() => {
    if (!active) {
      setBubbles([]);
      return undefined;
    }
    setBubbles((prev) => (prev.length ? prev : [spawnBubble()]));
    const interval = window.setInterval(() => {
      setBubbles((prev) => {
        const next = [...prev, spawnBubble()];
        // Cap to MAX_BUBBLES live bubbles; AnimatePresence will animate any
        // dropped from the array on out.
        return next.slice(-MAX_BUBBLES);
      });
    }, SPAWN_MS);
    return () => window.clearInterval(interval);
  }, [active]);

  const removeBubble = useCallback((key) => {
    setBubbles((prev) => prev.filter((b) => b.key !== key));
  }, []);

  const handleClick = useCallback(
    (bubble) => {
      if (onPick) onPick(bubble.set);
      removeBubble(bubble.key);
    },
    [onPick, removeBubble],
  );

  if (!active) return null;

  return (
    <Box
      className="edc-bubbles"
      aria-hidden="true"
      sx={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 1200, // above page chrome but below dialogs/AppBar (1300)
      }}
    >
      <AnimatePresence>
        {bubbles.map((b) => (
          <Box
            key={b.key}
            component={motion.div}
            className="edc-bubble"
            role="button"
            tabIndex={0}
            onClick={() => handleClick(b)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClick(b);
            }}
            initial={{ opacity: 0, scale: 0.6, y: -120 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: '115vh',
              transition: {
                opacity: { duration: 0.6, ease: 'easeOut' },
                scale: { duration: 0.6, ease: 'easeOut' },
                y: { duration: b.duration, ease: [0.45, 0.02, 0.55, 1] },
              },
            }}
            exit={{
              opacity: 0,
              scale: 0.3,
              transition: { duration: 0.5, ease: 'easeIn' },
            }}
            onAnimationComplete={(definition) => {
              // When the long fall completes, drop the bubble from state so
              // AnimatePresence runs the exit animation.
              if (definition && definition.y === '115vh') {
                removeBubble(b.key);
              }
            }}
            style={{
              '--edc-bubble-color': b.color,
              left: `${b.left}%`,
              width: `${b.size}px`,
            }}
            sx={{
              position: 'absolute',
              top: '-160px',
              minHeight: `${b.size * 0.82}px`,
              transform: 'translateX(-50%)',
              pointerEvents: 'auto',
              cursor: 'pointer',
              willChange: 'transform, opacity',
            }}
          >
            <Box
              component={motion.div}
              className="edc-bubble-skin"
              animate={{ rotate: [-2.5, 2.5, -2.5] }}
              transition={{
                duration: 3.4,
                ease: 'easeInOut',
                repeat: Infinity,
              }}
            >
              <Typography
                className="edc-bubble-time"
                component="div"
                sx={{ fontSize: '0.7rem', letterSpacing: '0.15em' }}
              >
                {getDayLabel(b.set.day).split(' ')[0].toUpperCase()} ·{' '}
                {formatSetTime(b.set.start)}
              </Typography>
              <Typography
                className="edc-bubble-artist"
                component="div"
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 800,
                  lineHeight: 1.1,
                  mt: 0.3,
                }}
              >
                {b.set.artist}
              </Typography>
              <Typography
                className="edc-bubble-stage"
                component="div"
                sx={{ fontSize: '0.65rem', opacity: 0.85, mt: 0.4 }}
              >
                {b.set.stage} · {b.set.genre.toUpperCase()}
              </Typography>
            </Box>
          </Box>
        ))}
      </AnimatePresence>
    </Box>
  );
}
